// 往来结算模块内的通用小件。现有 src/components 不够用的部分先写在这里，
// 其中 Table / Sheet / FilterSheet / Seg / Kpis 可能别的模块也要，建议主会话统一提取。
import { useState, type CSSProperties, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/Button';
import { money, num } from '@/lib/format';
import { isFuture, periodLabel, stepPeriod, useStlPerms, type Period } from './api';
import s from './ui.module.css';

/* ---------------- 顶部：导航栏 + 维度分段（客户往来 / 供应商往来 / 对账单） ---------------- */
type Seg4 = 'overview' | 'customer' | 'supplier' | 'statement';

function Segments({ active, keep, counts, phone = false, tablet = false }: { active: Seg4; keep?: string; counts?: Partial<Record<Seg4, number>>; phone?: boolean; tablet?: boolean }) {
  const p = useStlPerms();
  const q = keep ? `?period=${keep}` : '';
  const items: { key: Seg4; label: string; to: string; ok: boolean }[] = [
    { key: 'overview', label: '总览', to: '/settlements/overview', ok: p.overview },
    { key: 'customer', label: '客户往来', to: `/settlements/customers${q}`, ok: p.customer },
    { key: 'supplier', label: '供应商往来', to: `/settlements/suppliers${q}`, ok: p.supplier },
    { key: 'statement', label: '对账单', to: '/settlements/statements', ok: p.statement || p.followerView },
  ];
  return (
    <nav className={s.seg} aria-label="结算维度">
      {items.filter((i) => i.ok && !((phone || tablet) && i.key === 'overview')).map((i) => (
        <NavLink key={i.key} to={i.to} replace className={i.key === active ? s.on : undefined}>{i.label}{counts?.[i.key] !== undefined && <span className={s.segCount}>{counts[i.key]}</span>}</NavLink>
      ))}
    </nav>
  );
}

/** 手机：导航栏「‹ 应用」+ 居中标题 + 分段控件（高保真稿 T1）；桌面 / iPad：标题 + 分段 + 右侧动作 */
export function StlHeader({ phone, active, actions, keep, counts }: { phone: boolean; active: Seg4; actions?: ReactNode; keep?: string; counts?: Partial<Record<Seg4, number>> }) {
  const tablet = useFormFactor() === 'tablet';
  if (phone) {
    return (
      <>
        <header className={s.pnb}>
          <Link to="/apps" className={s.back}><Icon name="back" size={22} />应用</Link>
          <h1>往来结算</h1>
          <div className={s.pr}>{actions}</div>
        </header>
        <div className={s.segWrap}><Segments active={active} keep={keep} counts={counts} phone /></div>
      </>
    );
  }
  return (
    <header className={s.dh}>
      <h1>往来结算</h1>
      <Segments active={active} keep={keep} counts={counts} tablet={tablet} />
      <span className={s.sp} />
      {actions}
    </header>
  );
}

/* ---------------- 视图 chips（横滑，带计数） ---------------- */
export type ChipItem = { key: string; label: string; count?: number; sep?: boolean; arrow?: boolean };
export function Chips({ items, value, onChange }: { items: ChipItem[]; value: string; onChange: (k: string) => void }) {
  return (
    <div className={s.chips}>
      {items.map((c) => (
        <span key={c.key} className={s.chipWrap}>
          {c.sep && <i className={s.csep} />}
          {c.arrow && <Icon name="chev" size={14} className={s.carrow} />}
          <button type="button" aria-pressed={c.key === value} className={`${s.chip} ${c.key === value ? s.on : ''}`} onClick={() => onChange(c.key)}>
            {c.label}
            {c.count != null && <i className="tnum">{c.count}</i>}
          </button>
        </span>
      ))}
    </div>
  );
}

export function FilterButton({ count, onClick, boxed }: { count: number; onClick: () => void; boxed?: boolean }) {
  return (
    <button type="button" className={`${s.fbtn} ${boxed ? s.fboxed : ''} ${count ? s.fon : ''}`} onClick={onClick}>
      <Icon name="filter" />筛选{count > 0 && <span className={s.fbadge}>{count}</span>}
    </button>
  );
}

export function SearchBox({ value, onSubmit, placeholder, className }: { value: string; onSubmit: (v: string) => void; placeholder: string; className?: string }) {
  const [draft, setDraft] = useState(value);
  return (
    <form
      className={`${s.search} ${className || ''}`}
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(draft.trim());
      }}
    >
      <Icon name="srch" />
      <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={placeholder} enterKeyHint="search" />
      {draft && (
        <button type="button" className={s.clearQ} aria-label="清空" onClick={() => { setDraft(''); onSubmit(''); }}>
          <Icon name="x" size={14} />
        </button>
      )}
    </form>
  );
}

/* ---------------- 月份 + 日期口径：决定所有数字，常驻在汇总卡顶上（高保真稿 T1） ---------------- */
export function PeriodBar({ period, onPeriod, basis, basisOptions, onBasis, basisText }: {
  period: Period;
  onPeriod: (p: Period) => void;
  basis?: string;
  basisOptions?: { value: string; label: string }[];
  onBasis?: (v: string) => void;
  /** 不能改口径时只显示文字 */
  basisText?: string;
}) {
  return (
    <div className={s.pbar}>
      <div className={s.step}>
        {period.kind !== 'all' && <button type="button" aria-label="上一期" onClick={() => onPeriod(stepPeriod(period, -1))}><Icon name="back" size={16} /></button>}
        <span className="tnum"><Icon name="cal" size={14} />{periodLabel(period)}</span>
        {period.kind !== 'all' && <button type="button" aria-label="下一期" disabled={isFuture(period)} onClick={() => onPeriod(stepPeriod(period, 1))}><Icon name="chev" size={16} /></button>}
      </div>
      {basisOptions && onBasis ? (
        <label className={s.pick}>
          <i>口径</i>
          <span>{basisOptions.find((o) => o.value === basis)?.label || basis}</span>
          <Icon name="chevd" size={14} />
          {/* 原生下拉盖在上面、透明、16px：手机上弹系统选择器，iOS 不会因字号小于 16 而放大页面 */}
          <select value={basis} onChange={(e) => onBasis(e.target.value)} aria-label="日期口径">
            {basisOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
      ) : basisText ? (
        <span className={s.pick}><i>口径</i>{basisText}</span>
      ) : null}
    </div>
  );
}

/* ---------------- 数字格 ---------------- */
export type Kpi = { label: ReactNode; value: ReactNode; tone?: 'bad' | 'warn' | 'over' | 'blue' | 'good' | 'mute'; sub?: ReactNode };
/**
 * 数字格：宽时一行排开；窄容器（<600）按能整除的列数换行（3 或 2 列），不留空格子。
 * 格子之间用发丝线分隔。
 */
export function Kpis({ items, className }: { items: Kpi[]; className?: string }) {
  const n = items.length;
  const nn = n <= 3 ? n : n % 3 === 0 ? 3 : 2;
  const vars = { '--n': n, '--nn': nn } as CSSProperties;
  return (
    <div className={`${s.kwrap} ${className || ''}`}>
      <div className={s.kpis} style={vars}>
        {items.map((k, i) => (
          <div key={i} className={k.tone ? s[k.tone] : undefined}>
            <span>{k.label}</span>
            <b className="tnum">{k.value}</b>
            {k.sub && <small>{k.sub}</small>}
          </div>
        ))}
      </div>
    </div>
  );
}

/** 汇总格里的大数字去掉「.00」，其余与 money() 相同 */
export function bigMoney(v: unknown) {
  const n = num(v);
  const t = money(Math.abs(n)).replace(/\.00$/, '');
  return n < -0.004 ? `−${t}` : t;
}

/* ---------------- 面板 ---------------- */
export function Section({ title, extra, children, flush, className }: { title: ReactNode; extra?: ReactNode; children: ReactNode; flush?: boolean; className?: string }) {
  return (
    <section className={`${s.sec} ${className || ''}`}>
      <header className={s.secH}><h3>{title}</h3>{extra}</header>
      <div className={flush ? undefined : s.secB}>{children}</div>
    </section>
  );
}

/** 小分段（汇总版 / 明细版、卡片 / 表格） */
export function Seg<T extends string>({ options, value, onChange, small }: { options: { value: T; label: string }[]; value: T; onChange: (v: T) => void; small?: boolean }) {
  return (
    <div className={`${s.mseg} ${small ? s.msegSm : ''}`} role="tablist">
      {options.map((o) => (
        <button key={o.value} type="button" role="tab" aria-selected={o.value === value} className={o.value === value ? s.on : undefined} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

/** 「在老版中操作」：只读期所有写操作的去处 */
export function LegacyLink({ href, children, primary, icon, block, className }: { href: string; children: ReactNode; primary?: boolean; icon?: string; block?: boolean; className?: string }) {
  return (
    <a href={href} className={[s.legacy, primary && s.legacyP, block && s.legacyBlock, className].filter(Boolean).join(' ')} title="只读体验期：这一步在老版操作">
      {icon && <Icon name={icon} />}
      {children}
    </a>
  );
}

/* ---------------- 表格：首列吸左，金额右对齐（桌面 / iPad） ---------------- */
export type Col<T> = { key: string; title: ReactNode; w?: number; align?: 'r' | 'c'; fix?: boolean; fixRight?: boolean; left?: number; render: (r: T) => ReactNode };
export function Table<T>({ cols, rows, rowKey, onRow, selected, foot, dim }: {
  cols: Col<T>[];
  rows: T[];
  rowKey: (r: T) => string;
  onRow?: (r: T) => void;
  selected?: string;
  foot?: ReactNode[];
  dim?: (r: T) => boolean;
}) {
  const cls = (c: Col<T>) => [c.fix && s.fix, c.fixRight && s.fixRight, c.align === 'r' && s.r, c.align === 'c' && s.c].filter(Boolean).join(' ') || undefined;
  return (
    <div className={s.tscroll}>
      <table className={s.tbl}>
        <colgroup>{cols.map((c) => <col key={c.key} style={{ width: c.w }} />)}</colgroup>
        <thead><tr>{cols.map((c) => <th key={c.key} className={cls(c)} style={c.left === undefined ? undefined : { left: c.left }}>{c.title}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => {
            const k = rowKey(r);
            return (
              <tr key={k} className={[k === selected && s.sel, onRow && s.click, dim?.(r) && s.dim].filter(Boolean).join(' ') || undefined} onClick={onRow ? () => onRow(r) : undefined}>
                {cols.map((c) => <td key={c.key} className={cls(c)} style={c.left === undefined ? undefined : { left: c.left }}>{c.render(r)}</td>)}
              </tr>
            );
          })}
        </tbody>
        {foot && (
          <tfoot><tr>{foot.map((f, i) => <td key={i} className={cls(cols[i])}>{f}</td>)}</tr></tfoot>
        )}
      </table>
    </div>
  );
}

/* ---------------- 底部 sheet（手机）/ 右侧抽屉（iPad、桌面），文档 23 §6 ---------------- */
export function Sheet({ title, sheet, onClose, onReset, children, footer }: { title: string; sheet: boolean; onClose: () => void; onReset?: () => void; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className={s.scrim} onClick={onClose}>
      <aside className={sheet ? s.sheet : s.drawer} onClick={(e) => e.stopPropagation()} role="dialog" aria-label={title}>
        {sheet && <div className={s.grab} />}
        <header className={s.shd}>
          <button type="button" className={s.x} onClick={onClose} aria-label="关闭"><Icon name="x" size={20} /></button>
          <b>{title}</b>
          {onReset ? <button type="button" className={s.reset} onClick={onReset}>重置</button> : <span />}
        </header>
        <div className={s.sbd}>{children}</div>
        {footer && <footer className={s.sft}>{footer}</footer>}
      </aside>
    </div>
  );
}

export type FilterGroup = { key: string; title: string; required?: boolean; opts: { value: string; label: string; count?: number }[] };
/** 每组单选（老接口每个参数只收一个值）；required 的组不能取消选择 */
export function FilterSheet({ groups, value, defaults, onApply, onClose, sheet, hint }: {
  groups: FilterGroup[];
  value: Record<string, string>;
  defaults: Record<string, string>;
  onApply: (v: Record<string, string>) => void;
  onClose: () => void;
  sheet: boolean;
  hint?: ReactNode;
}) {
  const [f, setF] = useState<Record<string, string>>(value);
  return (
    <Sheet
      title="筛选"
      sheet={sheet}
      onClose={onClose}
      onReset={() => setF(defaults)}
      footer={<Button variant="primary" size="lg" block onClick={() => onApply(f)}>应用</Button>}
    >
      {groups.map((g) => (
        <section key={g.key} className={s.fsec}>
          <h4>{g.title}</h4>
          <div className={s.fchips}>
            {g.opts.map((o) => (
              <button
                key={o.value}
                type="button"
                className={`${s.fchip} ${f[g.key] === o.value ? s.on : ''}`}
                onClick={() => setF({ ...f, [g.key]: f[g.key] === o.value && !g.required ? defaults[g.key] : o.value })}
              >
                {o.label}{o.count != null && <i className="tnum">{o.count}</i>}
              </button>
            ))}
          </div>
        </section>
      ))}
      {hint && <p className={s.fhint}>{hint}</p>}
    </Sheet>
  );
}
